export function plainLine(item) {
  return !!item && !item.selling_plan_allocation && !item.parent_relationship && !item.item_components?.length && !Object.values(item.properties || {}).some(value => value != null && String(value) !== '');
}

export function resolveLine(cart, source) {
  const exact = cart.items.find(item => item.key === source.key && Number(item.variant_id) === Number(source.variant_id) && plainLine(item));
  if (exact) return exact;
  const matches = cart.items.filter(item => Number(item.variant_id) === Number(source.variant_id) && plainLine(item));
  return matches.length === 1 ? matches[0] : null;
}

export function removalPlan(cart, source, quantity, components, includeMatches) {
  const line = resolveLine(cart, source);
  if (!line || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > line.quantity) throw new Error('cart_changed');
  const ownComponent = components.find(component => Number(component.productId) === Number(line.product_id));
  if (!ownComponent || ownComponent.quantity !== 1) throw new Error('cart_changed');
  const plan = [{key:line.key,variantId:line.variant_id,quantity,previous:line.quantity,title:line.product_title || line.title,variantTitle:line.variant_title,unitPrice:(line.final_line_price ?? line.final_price * line.quantity) / line.quantity}];
  if (includeMatches) {
    for (const component of components) {
      if (Number(component.productId) === Number(line.product_id)) continue;
      let remaining = quantity * component.quantity;
      for (const item of cart.items) {
        if (!remaining) break;
        if (item.key === line.key || Number(item.variant_id) !== Number(component.id) || !plainLine(item)) continue;
        const take = Math.min(remaining,item.quantity);
        plan.push({key:item.key,variantId:item.variant_id,quantity:take,previous:item.quantity,title:item.product_title || item.title,variantTitle:item.variant_title,unitPrice:(item.final_line_price ?? item.final_price * item.quantity) / item.quantity});
        remaining -= take;
      }
    }
  }
  return plan;
}

const tokenLine = (cart,token) => cart.items.find(item => item.properties?._454_upgrade_token === token && !item.parent_relationship);

const cartSnapshot = cart => cart.items.map(item => ({variant_id:Number(item.variant_id),quantity:item.quantity,line_minor:item.final_line_price ?? item.final_price * item.quantity}));
const lineIdentity = item => JSON.stringify([Number(item.variant_id),item.selling_plan_allocation?.selling_plan?.id || null,Object.entries(item.properties || {}).sort(([a],[b])=>a.localeCompare(b))]);

export function upgradeMeasurement(before,after,plan,{token,quantity,bundleHandle,at}) {
  const expected = new Map();
  for (const item of before.items) {
    const removed = plan.filter(part=>part.key===item.key).reduce((sum,part)=>sum+part.quantity,0);
    const identity = lineIdentity(item);
    expected.set(identity,(expected.get(identity)||0)+item.quantity-removed);
  }
  const actual = new Map();
  for (const item of after.items) {
    if (item.properties?._454_upgrade_token === token) continue;
    const identity = lineIdentity(item);
    actual.set(identity,(actual.get(identity)||0)+item.quantity);
  }
  const sameOtherItems = [...new Set([...expected.keys(),...actual.keys()])].every(key=>(expected.get(key)||0)===(actual.get(key)||0));
  const bundle = tokenLine(after,token);
  const complete = !!bundle && bundle.quantity === quantity && sameOtherItems && before.currency === after.currency;
  return {v:1,token,bundle:bundleHandle,at,currency:before.currency,units:quantity,status:complete?'complete':'cart_changed',before_minor:before.total_price,after_minor:after.total_price,delta_minor:complete?after.total_price-before.total_price:null,replaced_minor:plan.reduce((sum,item)=>sum+Math.round(item.unitPrice*item.quantity),0),before_items:cartSnapshot(before),after_items:cartSnapshot(after)};
}

async function recordUpgrade(api,before,after,plan,details) {
  const event = upgradeMeasurement(before,after,plan,details);
  const amount = value => `${event.currency} ${(value/100).toFixed(2)}`;
  const summary = event.status === 'complete' ? `Before: ${amount(event.before_minor)} | After: ${amount(event.after_minor)} | Change: ${amount(event.delta_minor)} | ${event.units} x ${event.bundle}` : 'Cart changed during upgrade; review measurement.';
  const body = {attributes:{['__454_upsell_'+details.token]:JSON.stringify(event),['__454 Upsell '+details.token]:summary}};
  for (let attempt=0;attempt<2;attempt++) {
    try { await api.update(body); return after; } catch {}
  }
  return after;
}

export async function swapBundle(api,{source,quantity,bundleVariantId,bundleHandle,components,includeMatches,token}) {
  const before = await api.cart();
  const initialPlan = removalPlan(before,source,quantity,components,includeMatches);
  const at = new Date().toISOString();
  const details = {token,quantity,bundleHandle,at};
  const tracking = {_454_tracking_v:'1',_454_before_minor:String(before.total_price),_454_replaced_minor:String(initialPlan.reduce((sum,item)=>sum+Math.round(item.unitPrice*item.quantity),0)),_454_currency:before.currency,_454_upgrade_units:String(quantity),_454_source_variant:String(source.variant_id),_454_upgrade_at:at};
  try {
    await api.add({items:[{id:bundleVariantId,quantity,properties:{_454_upgrade_token:token,_454_bundle:bundleHandle,...tracking}}]});
  } catch (error) {
    let check;
    try { check = await api.cart(); } catch { throw new Error('recovery'); }
    if (!tokenLine(check,token)) throw error;
  }
  let afterAdd;
  try { afterAdd = await api.cart(); } catch { throw new Error('recovery'); }
  const added = tokenLine(afterAdd,token);
  if (!added) throw new Error('recovery');
  if (Number(added.quantity) !== quantity) {
    try { await api.change({id:added.key,quantity:0}); } catch { throw new Error('recovery'); }
    throw new Error('unavailable');
  }
  let plan;
  try {
    plan = removalPlan(afterAdd,source,quantity,components,includeMatches);
    const updates = Object.fromEntries(plan.map(item => [item.key,item.previous-item.quantity]));
    await api.update({updates});
  } catch (error) {
    let current;
    try { current = await api.cart(); } catch { throw new Error('recovery'); }
    const totals = cart => new Map(cart.items.filter(plainLine).map(item=>[Number(item.variant_id),cart.items.filter(line=>Number(line.variant_id)===Number(item.variant_id)&&plainLine(line)).reduce((n,line)=>n+line.quantity,0)]));
    const initialTotals=totals(afterAdd),currentTotals=totals(current),removed=new Map();
    plan?.forEach(item=>removed.set(Number(item.variantId),(removed.get(Number(item.variantId))||0)+item.quantity));
    const completed = plan && [...removed].every(([id,count])=>(currentTotals.get(id)||0)===(initialTotals.get(id)||0)-count);
    if (completed) return recordUpgrade(api,before,current,initialPlan,details);
    const partial = plan && [...removed].some(([id])=>(currentTotals.get(id)||0)!==(initialTotals.get(id)||0));
    if (partial) throw new Error('recovery');
    const rollback = tokenLine(current,token);
    if (rollback) {
      try { await api.change({id:rollback.key,quantity:0}); } catch { throw new Error('recovery'); }
    }
    throw error;
  }
  const after = await api.cart();
  return recordUpgrade(api,before,after,initialPlan,details);
}

if (typeof document !== 'undefined') {
  const configElement = document.getElementById('bu454-config');
  const dialog = document.getElementById('bu454-dialog');
  if (configElement && dialog && !window.F454BundleUpgrade) {
    const config = JSON.parse(configElement.textContent);
    const t = (key,values={}) => Object.entries(values).reduce((text,[name,value]) => text.replaceAll(`{${name}}`,String(value)),config.texts[key] || key);
    const root = window.Shopify?.routes?.root || '/';
    const get = selector => dialog.querySelector(selector);
    const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en-US',{style:'currency',currency:state.cart?.currency || config.currency}).format(value/100);
    const state = {busy:false,context:null,cart:null,offers:[],offer:null,variant:null,quote:null,recovery:false};
    let catalogPromise;
    const products = new Map();
    const request = async (path,body) => {
      const response = await fetch(root+path,{method:body?'POST':'GET',credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json',...(body?{'Content-Type':'application/json'}:{})},...(body?{body:JSON.stringify(body)}:{}),signal:AbortSignal.timeout(12000)});
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || t('error'));
      return data;
    };
    const api = {cart:()=>request('cart.js'),add:body=>request('cart/add.js',body),update:body=>request('cart/update.js',body),change:body=>request('cart/change.js',body)};
    const catalog = () => catalogPromise ||= fetch(config.catalog,{credentials:'omit',signal:AbortSignal.timeout(8000)}).then(response=>{if(!response.ok)throw new Error('error');return response.json();}).catch(error=>{catalogPromise=null;throw error;});
    const product = (handle,fresh=false) => {
      if (fresh || !products.has(handle)) products.set(handle,request(`products/${encodeURIComponent(handle)}.js`).catch(error=>{products.delete(handle);throw error;}));
      return products.get(handle);
    };
    const element = (tag,text,className) => {const node=document.createElement(tag);if(text!=null)node.textContent=text;if(className)node.className=className;return node;};
    const mappedVariant = () => state.offer?.mapping.variants.find(variant=>Number(variant.id)===Number(state.variant?.id));
    const setError = message => {get('[data-bu-error]').textContent=message;get('[data-bu-error]').hidden=!message;};
    const hasBundle = (cart,mapping) => cart.items.some(item=>Number(item.product_id)===mapping.id || item.properties?._454_bundle===mapping.handle);
    const label = name => name.toLowerCase().includes('size')?t('bag_size'):name.toLowerCase().includes('ties')?t('ties'):name.toLowerCase().includes('tote')?t('tote_quantity'):name.toLowerCase().includes('curepak')?t('curepak_count'):name.toLowerCase().includes('turkey')?t('barrier_count'):t('bag_count');
    const quantity = () => Number(get('[data-bu-quantity]').value);
    function chooseBundle(index) {
      state.offer=state.offers[index];
      state.variant=state.offer.preferred;
      const image=get('[data-bu-image]');image.src=state.offer.product.featured_image || state.offer.product.images?.[0] || '';image.alt=state.offer.product.title;
      const options=get('[data-bu-options]');options.replaceChildren();
      state.offer.product.options.forEach((option,index)=>{
        const name=typeof option==='string'?option:option.name;
        const wrap=element('div',null,'bu454-option');
        const text=element('label',label(name));text.htmlFor=`bu454-option-${index}`;
        const select=element('select');select.id=text.htmlFor;select.dataset.option=index;
        [...new Set(state.offer.product.variants.map(variant=>variant.options[index]))].forEach(value=>{const opt=element('option',value);opt.value=value;select.append(opt);});
        select.value=state.variant.options[index];
        select.addEventListener('change',()=>{
          const selected=[...options.querySelectorAll('select')].map(input=>input.value);
          state.variant=state.offer.product.variants.find(variant=>variant.options.every((value,i)=>value===selected[i]));
          draw();
        });
        wrap.append(text,select);options.append(wrap);
      });
      draw();
    }
    function draw() {
      state.quote=null;
      const variant=state.variant,mapped=mappedVariant();
      const q=quantity();
      const valid=variant?.available && mapped && Number.isSafeInteger(q) && q>0 && q<=state.context.quantity;
      get('[data-bu-upgrade]').disabled=!valid || state.busy;
      if(!valid){setError(t('unavailable'));return;}
      setError('');
      const allMatches=removalPlan(state.cart,state.context.source,q,mapped.components,true);
      get('[data-bu-match-wrap]').hidden=allMatches.length<2;
      get('[data-bu-match-list]').textContent=allMatches.slice(1).map(item=>`${item.quantity} × ${item.title}${item.variantTitle&&item.variantTitle!=='Default Title'?` · ${item.variantTitle}`:''}`).join('; ');
      const plan=get('[data-bu-match]').checked?allMatches:allMatches.slice(0,1);
      const contents=get('[data-bu-contents]');contents.replaceChildren();
      let separate=0;
      for(const component of mapped.components){
        const p=state.offer.components.get(component.handle);
        const cv=p.variants.find(v=>Number(v.id)===component.id);
        if(!cv){setError(t('unavailable'));get('[data-bu-upgrade]').disabled=true;return;}
        separate+=cv.price*component.quantity*q;
        contents.append(element('li',`${component.quantity*q} × ${p.title}${cv.title!=='Default Title'?` · ${cv.title}`:''}`));
      }
      const total=variant.price*q,saving=separate-total;
      if(saving<=0){setError(t('unavailable'));get('[data-bu-upgrade]').disabled=true;return;}
      const percent=Math.round(saving/separate*100);
      const removed=plan.reduce((sum,item)=>sum+Math.round(item.unitPrice*item.quantity),0);
      const delta=total-removed;
      get('[data-bu-badge]').textContent=t('save_badge',{percent});
      get('[data-bu-separate]').textContent=money(separate);
      get('[data-bu-total]').textContent=money(total);
      get('[data-bu-saving]').textContent=`${money(saving)} (${percent}%)`;
      get('[data-bu-delta]').textContent=delta>0?t('cart_more',{amount:money(delta)}):delta<0?t('cart_less',{amount:money(-delta)}):t('cart_same');
      const changed=!mapped.components.some(component=>component.id===Number(state.context.source.variant_id));
      get('[data-bu-replaces]').textContent=t('replaces',{items:plan.map(item=>`${item.quantity} × ${item.title}`).join('; ')})+(changed?' '+t('changed_options'):'');
      state.quote={variantId:variant.id,price:variant.price,q,includeMatches:get('[data-bu-match]').checked,components:mapped.components};
    }
    async function openFor(source,cart,q,onDismiss) {
      if(state.busy || dialog.open || !plainLine(source))return false;
      const data=await catalog();
      const eligible=data.bundles.filter(bundle=>!hasBundle(cart,bundle)&&bundle.variants.some(variant=>variant.components.some(component=>component.id===Number(source.variant_id)&&component.quantity===1)));
      if(!eligible.length)return false;
      const offers=(await Promise.all(eligible.map(async mapping=>{
        const p=await product(mapping.handle,true);
        const matchIds=new Set(mapping.variants.filter(variant=>variant.components.some(component=>component.id===Number(source.variant_id))).map(variant=>variant.id));
        const preferred=p.variants.filter(variant=>variant.available&&matchIds.has(Number(variant.id))).sort((a,b)=>a.price-b.price)[0];
        if(!preferred)return null;
        const handles=[...new Set(mapping.variants.flatMap(variant=>variant.components.map(component=>component.handle)))];
        const cps=await Promise.all(handles.map(async handle=>[handle,await product(handle,true)]));
        return {mapping,product:p,preferred,components:new Map(cps)};
      }))).filter(Boolean).sort((a,b)=>a.preferred.price-b.preferred.price);
      if(!offers.length)return false;
      state.context={source,quantity:Math.min(q,source.quantity),onDismiss};state.cart=cart;state.offers=offers;state.recovery=false;
      get('[data-bu-current]').textContent=t('current',{quantity:state.context.quantity,product:source.product_title||source.title,variant:source.variant_title&&source.variant_title!=='Default Title'?source.variant_title:''});
      const select=get('[data-bu-bundle]');select.replaceChildren();offers.forEach((offer,index)=>{const option=element('option',offer.product.title);option.value=index;select.append(option);});
      get('[data-bu-quantity]').value=state.context.quantity;get('[data-bu-quantity]').max=state.context.quantity;get('[data-bu-match]').checked=true;get('[data-bu-button-text]').textContent=t('upgrade');
      get('[data-bu-controls]').disabled=false;chooseBundle(0);dialog.showModal();get('[data-bu-bundle]').focus();return true;
    }
    function close(){if(state.busy)return;if(state.recovery){location.assign(root+'cart');return;}dialog.close();const callback=state.context?.onDismiss;state.context=null;callback?.();}
    dialog.addEventListener('cancel',event=>{event.preventDefault();close();});
    dialog.addEventListener('click',event=>{if(event.target===dialog){const rect=dialog.getBoundingClientRect();if(event.clientX<rect.left||event.clientX>rect.right||event.clientY<rect.top||event.clientY>rect.bottom)close();}});
    dialog.querySelectorAll('[data-bu-close]').forEach(button=>button.addEventListener('click',close));
    get('[data-bu-bundle]').addEventListener('change',event=>chooseBundle(Number(event.target.value)));
    get('[data-bu-quantity]').addEventListener('input',draw);get('[data-bu-match]').addEventListener('change',draw);
    get('[data-bu-upgrade]').addEventListener('click',async()=>{
      if(state.busy)return;
      if(state.recovery){location.assign(root+'cart');return;}
      const quote=state.quote;if(!quote || get('[data-bu-upgrade]').disabled)return;
      state.busy=true;get('[data-bu-controls]').disabled=true;get('[data-bu-upgrade]').disabled=true;get('[data-bu-button-text]').textContent=t('working');setError('');
      try{
        const latest=await product(state.offer.mapping.handle,true);const variant=latest.variants.find(v=>Number(v.id)===Number(quote.variantId));
        if(!variant?.available)throw new Error('unavailable');if(variant.price!==quote.price)throw new Error('price_changed');
        await swapBundle(api,{source:state.context.source,quantity:quote.q,bundleVariantId:quote.variantId,bundleHandle:state.offer.mapping.handle,components:quote.components,includeMatches:quote.includeMatches,token:crypto.randomUUID()});
        location.assign(root+'cart');
      }catch(error){
        state.recovery=error.message==='recovery';setError(config.texts[error.message]?t(error.message):error.message||t('error'));
        get('[data-bu-button-text]').textContent=state.recovery?t('view_cart'):t('upgrade');
      }finally{state.busy=false;get('[data-bu-controls]').disabled=state.recovery;get('[data-bu-upgrade]').disabled=false;}
    });
    const afterAdd=async({variantId,quantity=1,sellingPlan,onDismiss})=>{
      if(sellingPlan)return false;
      try{const data=await catalog();if(!data.bundles.some(bundle=>bundle.variants.some(variant=>variant.components.some(component=>component.id===Number(variantId)))))return false;
        const cart=await api.cart();const candidates=cart.items.filter(item=>Number(item.variant_id)===Number(variantId)&&plainLine(item));if(candidates.length!==1)return false;
        return await openFor(candidates[0],cart,quantity,onDismiss);
      }catch{return false;}
    };
    window.F454BundleUpgrade={afterAdd};
    const hydrate=async()=>{
      const entries=document.getElementById('bu454-cart-entries');
      if(entries)for(const entry of entries.content.querySelectorAll('[data-bu-handle]')){
        const button=entry.querySelector('[data-bu-line]');
        if(!button || [...document.querySelectorAll('[data-bu-line]')].some(existing=>existing.dataset.buLine===button.dataset.buLine))continue;
        const anchor=[...document.querySelectorAll('#MainContent a[href]')].find(link=>link.textContent.trim()&&new URL(link.href,location.origin).pathname.endsWith('/products/'+entry.dataset.buHandle));
        if(anchor)anchor.after(button.cloneNode(true));
      }
      const buttons=[...document.querySelectorAll('[data-bu-line]')];if(!buttons.length)return;
      try{const data=await catalog();const ids=new Set(data.bundles.flatMap(bundle=>bundle.variants.flatMap(variant=>variant.components.map(component=>component.id))));buttons.forEach(button=>{button.hidden=!ids.has(Number(button.dataset.buVariant));});}catch{}
    };
    document.addEventListener('click',async event=>{
      const button=event.target.closest('[data-bu-line]');if(!button || button.disabled)return;button.disabled=true;
      try{const cart=await api.cart();const source=cart.items.find(item=>item.key===button.dataset.buLine&&plainLine(item));if(source)await openFor(source,cart,source.quantity,()=>button.focus());}catch{location.assign(root+'cart');}finally{button.disabled=false;}
    });
    let scheduled=false;
    new MutationObserver(()=>{if(!scheduled){scheduled=true;requestAnimationFrame(()=>{scheduled=false;hydrate();});}}).observe(document.body,{childList:true,subtree:true});
    hydrate();
  }
}


